<template>
  <div
    class="bg-muted-100 dark:bg-muted-900 relative w-full overflow-hidden px-4"
  >
    <BaseProgress
      v-if="page.isBusy"
      title="indeterminate primary progress bar"
      size="xs"
      class="fixed left-0 top-0 z-50 h-2 w-full"
    />
    <div class="flex w-full items-center justify-center">
      <div class="relative mx-auto flex w-full max-w-2xl flex-col items-center">
        <SkyVisitorLogo />
        <div class="me-auto ms-auto w-full">
          <Form
            ref="formRef"
            v-slot="{ values }"
            method="POST"
            action=""
            class="mx-auto me-auto ms-auto w-full max-w-lg px-4"
            @submit="onSubmit"
          >
            <div class="px-8 pb-4">
              <div>
                <Field
                  v-slot="{ field, errorMessage, handleChange, handleBlur }"
                  name="username"
                  type="email"
                  :rules="validateRequired"
                >
                  <BaseInput
                    :model-value="field.value"
                    :error="errorMessage"
                    :disabled="page.isBusy"
                    type="email"
                    :label="$t('login.username')"
                    :placeholder="$t('login.username')"
                    :classes="{
                      input: 'h-12',
                    }"
                    @update:model-value="handleChange"
                    @blur="handleBlur"
                  />
                </Field>
              </div>
              <div class="my-6 flex justify-between space-x-4">
                <div class="w-1/2">
                  <BaseButton
                    type="button"
                    to="/"
                  >
                    {{ $t("auth.button_go_back") }}
                  </BaseButton>
                </div>
                <div class="w-1/2">
                  <BaseButton
                    :disabled="page.isBusy"
                    :loading="page.isBusy"
                    type="submit"
                    color="primary"
                    class="w-full"
                  >
                    {{ $t("auth.button_send_token") }}
                  </BaseButton>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Field, Form } from 'vee-validate'
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
useHead({
  title: 'Login',
})

definePageMeta({
  breadcrumb: {
    label: 'Admin',
  },
})

const { locale, t } = useI18n()

// Get the store instance
const {
  clear: clearAuth,
  user: authStoreUser,
  clear,
  setUser,
  setUserTenants,
} = useAuthStore()

const router = useRouter()
const { $dataService, $notification } = useNuxtApp()

// Watch the value of the dropdown and set i18n accordingly
watch(locale, () => {
  requireMessage.value = t('validation.required')
  resetForm() // HACK: Hide all error messages
})

definePageMeta({
  title: 'Login',
  preview: {
    title: 'SkyVisitor Password Reset',
    description: 'Resetting password',
    categories: ['authentication'],
    src: '/img/screens/auth-login-3.png',
    srcDark: '/img/screens/auth-login-3-dark.png',
    order: 98,
  },
})

onMounted(() => {
  page.isBusy = false
})

const requireMessage = ref('')
const validateRequired = (value) => {
  // If the field is empty
  if (!value) {
    requireMessage.value = t('validation.required')
    return requireMessage.value
  }
  // All is good
  return true
}
// Reference for the form to reset it
const formRef = ref(null)
const resetForm = () => {
  formRef.value.resetForm() // This will reset the form
}

const page = reactive({
  isBusy: true,
  isEmailValidated: false,
})

const onSubmit = async (values) => {
  page.isBusy = true
  await setUser({
    login: values.username,
  })
  return await sendResetToken(values.username)
}

const sendResetToken = async (username) => {
  let tenant
  let login = username
  page.isBusy = true
  // Checks whether a tenant is specified
  if (username.includes('.')) {
    // Split the username from the tenant
    const loginSplit = username.split('.')
    tenant = loginSplit[0]
    login = loginSplit.slice(1).join('.')
  }

  try {
    const authResult = await $dataService.createPasswordResetToken(login, tenant)

    return navigateTo('/password/token')
  }
  catch (error) {
    setTimeout(() => {
      page.isBusy = false
    }, 1500)

    // TODO: Log the failure?
    $notification.error({
      ...error,
      message: t('auth.token_not_sent'),
    })
  }
  finally {
    page.isBusy = false
  }

  return { tenant, login }
}

async function redirectToSkyVisitor(user, tenantPrefix) {
  let tenants
  let rootDomain = window.location.hostname
  const index = rootDomain.indexOf('.')
  if (index > -1) rootDomain = rootDomain.substring(index)
  if (tenantPrefix) {
    const cookie = `tenant=${tenantPrefix};expires=${new Date(Date.now() + 86400000)};path=/;domain=${rootDomain};`
    document.cookie = cookie
  }
  page.isBusy = true
  // If it's a normal user (tenant not specified),
  //  then we need to get the tenants
  if (!tenantPrefix) {
    const tenantUsers = await $dataService.findTenantUsers({
      query: {
        userId: user.id,
        $limit: 5000,
      },
    })

    // If we have multiple tenants, redirect to selection
    if (tenantUsers.total == 0) {
      // ! Big problem - What do we do?
    }
    else if (tenantUsers.total > 1) {
      // Store the tenants and redirect to tenants page
      getTenantsAndRedirect(user)
      return // cancel any further code
    }
    else {
      // Just one tenant, so log in normally
      const tenantUserData = tenantUsers.data[0]
      // Get the tenant by tenantId
      tenants = await $dataService.findTenants({
        query: {
          id: tenantUserData.tenantId,
        },
      })
    }
  }
  else {
    // Get the tenant by database name
    tenants = await $dataService.findTenants({
      query: {
        database: tenantPrefix,
        $limit: 5000,
      },
    })
  }

  // Check for the goodness
  if (tenants.total != 1) {
    // ! This database likely exist on multiple servers...not good!
    throw `Database found on these servers, can't decide: ${JSON.stringify(
      tenants.data.map(t => t.server),
    )}`
  }

  const tenantRecord = tenants.data[0]
  try {
    const redirectUrl = await $dataService.findSkyVisitor({
      query: {
        login: user.login,
        server: tenantRecord.server,
        database: tenantRecord.database,
      },
    })
    // Add the auth token to the local storage
    window.location.href = redirectUrl.url
  }
  catch (error) {
    $notification.error({
      ...error,
      message: t('auth.skyvisitor_redirect_error'),
    })
  }
  finally {
    page.isBusy = false
  }
}

function getTenantsAndRedirect(user) {
  // console.log('User', user)
  $dataService
    .findTenantUsers({
      query: {
        userId: user.id,
      },
    })
    .then(async (tenantUserResults) => {
      // console.log('>>> tenantUserResults', tenantUserResults)
      if (tenantUserResults.total == 0) {
        // ERROR: No tenants!
        throw 'auth.no_tenant'
      }
      else if (tenantUserResults.total == 1) {
        // Only one tenant, so redirect to SkyV - NOT tenants selection
        const tenantUserRecord = tenantUserResults.data[0]

        const tenants = await $dataService.findTenants({
          query: {
            id: tenantUserRecord.tenantId,
          },
        })
        const tenantData = tenants.data[0]

        await redirectToSkyVisitor(user, tenantData.database)
      }
      else {
        // Redirect to Tenant selection
        setUserTenants(tenantUserResults.data)
        await navigateTo('/tenants')
      }
    })
}
// TODO: Set default expiration
//        Re-auth before then if "remember me" checked
//        This will then auto-expire
// TODO: Also find this in the Auth service and implement blacklisting
// Call processAuthentication() on initialization
// processAuthentication()
</script>
